import {
  AutoCompleteMultiUncontrolled,
  DatePickerUncontrolled,
  DateRangeSelect,
  ISelectType,
} from "@4uhub/lib4uhub";
import { Box, FormHelperText, Stack, TextField } from "@mui/material";
import React, {
  Dispatch,
  SetStateAction,
  memo,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import FiltersActions from "../../../../components/FiltersActions/FiltersActions";
import useFilter from "../../../../hooks/useFilter";
import {
  CUSTOM_DATE_RANGE_OPTION_ID,
  DEFAULT_DATE_RANGE_OPTION_ID,
} from "./ticketHistoriesFilters";

export interface IFilterParams {
  DateRangeOptionId: string;
  StartDate: Date | null;
  EndDate: Date | null;
  OccurrenceTypeIds: string[];
  TicketServiceIds: string[];
  Search: string;
  refresh?: boolean;
}

interface IMacroFilterProps {
  onFilterParams: Dispatch<SetStateAction<IFilterParams>>;
  filterParams: IFilterParams;
  occurrenceTypeOptions: ISelectType[];
  ticketServiceOptions: ISelectType[];
}

interface ITicketHistoryFilterErrors {
  startDate?: string;
  endDate?: string;
  occurrenceType?: string;
}

const translationPath = "chatBot.page.logs.ticketHistories.";

const MacroFilter: React.FC<IMacroFilterProps> = ({
  onFilterParams,
  filterParams,
  occurrenceTypeOptions,
  ticketServiceOptions,
}) => {
  const {
    disableFilterBtn: defaultDisableFilterBtn,
    filter,
    showCleanBtn,
    initialState,
    setFilter,
    cleanFilter,
  } = useFilter(filterParams);

  const { t } = useTranslation();

  const [resetDateRange, setResetDateRange] = useState(false);
  const [enableFields, setEnableFields] = useState(true);
  const [filterErrors, setFilterErrors] = useState<ITicketHistoryFilterErrors>(
    {},
  );

  const dateRangeOptionIdRef = useRef(filter.DateRangeOptionId);
  dateRangeOptionIdRef.current = filter.DateRangeOptionId;

  const isCustomDateRangeSelected =
    !enableFields || filter.DateRangeOptionId === CUSTOM_DATE_RANGE_OPTION_ID;

  const selectedOccurrenceTypes = useMemo(
    () =>
      occurrenceTypeOptions.filter((option) =>
        filter.OccurrenceTypeIds.includes(option.id),
      ),
    [filter.OccurrenceTypeIds, occurrenceTypeOptions],
  );

  const selectedTicketServices = useMemo(
    () =>
      ticketServiceOptions.filter((option) =>
        filter.TicketServiceIds.includes(option.id),
      ),
    [filter.TicketServiceIds, ticketServiceOptions],
  );

  const validateFilter = (
    nextFilter: IFilterParams,
    customDateRangeSelected = isCustomDateRangeSelected,
  ): ITicketHistoryFilterErrors => {
    const errors: ITicketHistoryFilterErrors = {};

    if (!nextFilter.OccurrenceTypeIds.some((id) => Boolean(id))) {
      errors.occurrenceType =
        t(translationPath + "errors.occurrence_type_required") ?? "";
    }

    if (customDateRangeSelected) {
      if (!nextFilter.StartDate) {
        errors.startDate =
          t(translationPath + "errors.start_date_required") ?? "";
      }

      if (!nextFilter.EndDate) {
        errors.endDate = t(translationPath + "errors.end_date_required") ?? "";
      }

      if (
        nextFilter.StartDate &&
        nextFilter.EndDate &&
        nextFilter.StartDate > nextFilter.EndDate
      ) {
        errors.endDate = t(translationPath + "errors.invalid_date_range") ?? "";
      }
    }

    return errors;
  };

  const updateFilter = (
    changes: Partial<IFilterParams>,
    customDateRangeSelected = isCustomDateRangeSelected,
  ) => {
    const nextFilter = { ...filter, ...changes };

    setFilter(changes);

    if (Object.keys(filterErrors).length > 0) {
      setFilterErrors(validateFilter(nextFilter, customDateRangeSelected));
    }
  };

  const occurrenceTypeError = filterErrors.occurrenceType;
  const startDateError = filterErrors.startDate;
  const endDateError = filterErrors.endDate;

  const currentValidationErrors = validateFilter(filter);
  const disableFilterBtn =
    Object.keys(currentValidationErrors).length > 0
      ? false
      : defaultDisableFilterBtn;

  const filterHandler = () => {
    const nextErrors = validateFilter(filter);

    setFilterErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    if (defaultDisableFilterBtn) {
      return;
    }

    onFilterParams(filter);
  };

  const cleanHandler = () => {
    onFilterParams(initialState.current);
    cleanFilter();
    setFilterErrors({});
    setResetDateRange((prev) => !prev);
  };

  return (
    <Stack
      flexDirection="row"
      gap={1.5}
      alignItems="flex-start"
      flexWrap="wrap"
      sx={{ width: "100%" }}
    >
      <Box
        sx={{
          flex: "0 1 220px",
          minWidth: 180,
          maxWidth: 220,
          "& > *": { width: "100%" },
          "& .MuiFormControl-root": { width: "100%" },
        }}
      >
        <DateRangeSelect
          setEndDate={(date) => {
            if (dateRangeOptionIdRef.current === CUSTOM_DATE_RANGE_OPTION_ID) {
              return;
            }

            updateFilter({ EndDate: date }, false);
          }}
          setStartDate={(date) => {
            if (dateRangeOptionIdRef.current === CUSTOM_DATE_RANGE_OPTION_ID) {
              return;
            }

            updateFilter({ StartDate: date }, false);
          }}
          setEnableFields={setEnableFields}
          resetToToday={resetDateRange}
          onValueSelected={(option) => {
            const nextDateRangeOptionId =
              option?.id ?? DEFAULT_DATE_RANGE_OPTION_ID;

            const nextIsCustomDateRange =
              nextDateRangeOptionId === CUSTOM_DATE_RANGE_OPTION_ID;

            dateRangeOptionIdRef.current = nextDateRangeOptionId;

            updateFilter(
              {
                DateRangeOptionId: nextDateRangeOptionId,
                ...(nextIsCustomDateRange
                  ? {
                      StartDate: null,
                      EndDate: null,
                    }
                  : {}),
              },
              nextIsCustomDateRange,
            );
          }}
        />
      </Box>

      <Box
        sx={{
          flex: "0 1 190px",
          minWidth: 160,
          maxWidth: 190,
          "& .MuiFormControl-root": { width: "100%" },
          ...(startDateError
            ? {
                "& .MuiInputLabel-root": {
                  color: "error.main",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "error.main !important",
                },
                "& .MuiSvgIcon-root": {
                  color: "error.main",
                },
              }
            : {}),
        }}
      >
        <Stack spacing={0.5}>
          <DatePickerUncontrolled
            value={filter.StartDate}
            onChange={(date) => updateFilter({ StartDate: date })}
            label={t(translationPath + "start_date") ?? ""}
            isClearable={false}
            disable={enableFields}
          />
          {startDateError && (
            <FormHelperText error>{startDateError}</FormHelperText>
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          flex: "0 1 190px",
          minWidth: 160,
          maxWidth: 190,
          "& .MuiFormControl-root": { width: "100%" },
          ...(endDateError
            ? {
                "& .MuiInputLabel-root": {
                  color: "error.main",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "error.main !important",
                },
                "& .MuiSvgIcon-root": {
                  color: "error.main",
                },
              }
            : {}),
        }}
      >
        <Stack spacing={0.5}>
          <DatePickerUncontrolled
            value={filter.EndDate}
            onChange={(date) => updateFilter({ EndDate: date })}
            label={t(translationPath + "end_date") ?? ""}
            isClearable={false}
            disable={enableFields}
            minDate={filter.StartDate ?? undefined}
          />
          {endDateError && (
            <FormHelperText error>{endDateError}</FormHelperText>
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          flex: "0 1 360px",
          minWidth: 260,
          maxWidth: 360,
        }}
      >
        <AutoCompleteMultiUncontrolled
          label={t(translationPath + "occurrence_type") ?? ""}
          onValueSelected={(value) => {
            updateFilter({
              OccurrenceTypeIds: value?.map((item) => item.id) ?? [],
            });
          }}
          value={selectedOccurrenceTypes}
          options={occurrenceTypeOptions}
          size="small"
          fullWidth
          limitTags={1}
          error={Boolean(occurrenceTypeError)}
          helperText={occurrenceTypeError}
          sx={{ width: "100%" }}
        />
      </Box>

      <Box
        sx={{
          flex: "0 1 320px",
          minWidth: 260,
          maxWidth: 320,
        }}
      >
        <AutoCompleteMultiUncontrolled
          label={t(translationPath + "ticket_service") ?? ""}
          onValueSelected={(value) =>
            updateFilter({
              TicketServiceIds: value?.map((item) => item.id) ?? [],
            })
          }
          value={selectedTicketServices}
          options={ticketServiceOptions}
          size="small"
          fullWidth
          limitTags={1}
          sx={{ width: "100%" }}
        />
      </Box>

      <Box
        sx={{
          flex: "0 1 auto",
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          minWidth: 340,
          maxWidth: 460,
        }}
      >
        <TextField
          value={filter.Search}
          size="small"
          label={t(translationPath + "search") ?? ""}
          sx={{
            width: 300,
            minWidth: 260,
          }}
          onChange={(e) => updateFilter({ Search: e.target.value })}
        />

        <Box
          sx={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            minHeight: 40,
          }}
        >
          <FiltersActions
            disableFilterBtn={disableFilterBtn}
            onFilter={filterHandler}
            onClean={cleanHandler}
            showCleanBtn={showCleanBtn}
          />
        </Box>
      </Box>
    </Stack>
  );
};

export default memo(MacroFilter);
